import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RoutesRecognized } from '@angular/router';
import { Store } from '../../../store';
import { QueryConfig } from '../../../core/models/QueryConfig';
import * as _ from 'underscore';
import { User } from '../../../core/models/User';
import { DataService } from '../../../core/services/data.service';
import { ApplicationEventsService } from '../../../core/services/application-events.service';
import { saveAs } from 'file-saver';
import { tap, takeUntil, filter, switchMap } from 'rxjs/operators';
import { Subject, combineLatest } from 'rxjs';
import { isNil } from 'lodash-es';
import { UtilityService } from '../../../core/services/utility.service';
import { of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ErrorPopupComponent } from '../error-popup/error-popup.component';

@Component({
    selector: 'app-tool-bar',
    templateUrl: './tool-bar.component.html',
    styleUrls: ['./tool-bar.component.scss'],
    standalone: false
})
export class ToolBarComponent implements OnInit {
    title: string;
    showModelOverlay = false;
    showModelBar = false;
    modellingConfig: QueryConfig;
    legalEntityName: string;
    legalEntityId: string;
    counterPartyName: string;
    minTail: number;
    maxTail: number;
    apiUrl: string;
    user: User = null;
    trades: any[] = [];
    temp: any[] = [];
    tradeDataLoading: boolean;
    selectedTrade: any = null;
    private unsubscribe: Subject<void> = new Subject();
    isLoading = false;

    @ViewChild('errorPopup') errorPopup!: ErrorPopupComponent;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private store: Store,
        private dataService: DataService,
        private appEvents: ApplicationEventsService,
        private utility: UtilityService
    ) {}

    ngOnInit() {
        const user$ = this.store.select<User>('user').pipe(
            filter((user) => !isNil(user)),
            tap((user) => (this.user = user))
        );
        const routes$ = this.router.events.pipe(
            filter((val) => val instanceof RoutesRecognized)
        );
        combineLatest(user$, routes$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(([user, route]) => {
                const val = route as RoutesRecognized;
                this.showModelOverlay = false;
                this.legalEntityId = val.state.root.firstChild.params.entityId;
                this.legalEntityName = this.computeLegalEntityName(
                    this.legalEntityId
                );
                this.counterPartyName =
                    val.state.root.firstChild.params.counterPartyId;
                this.getTitle(val.url);
                this.getUrlModelStatus(val.url);
            });
        this.apiUrl = environment.apiUrl;

        const selectedModel$ = this.store
            .select<QueryConfig>('modellingConfig')
            .pipe(
                takeUntil(this.unsubscribe),
                switchMap((queryConfig: QueryConfig) => {
                    if (queryConfig.modelId) {
                        return this.dataService.getTradeInformation(
                            queryConfig.modelId
                        );
                    } else {
                        return of(null);
                    }
                })
            );
        selectedModel$.subscribe((tradeSimulation: any) => {
            if (tradeSimulation && tradeSimulation.tradeInstruments) {
                let found = false;
                for (const instrument of tradeSimulation.tradeInstruments) {
                    if (instrument.entity === this.legalEntityName) {
                        this.selectedTrade = {
                            modelId: instrument.modelId,
                            modelName: instrument.modelName,
                        };
                        found = true;
                        break;
                    }
                    if (!found) {
                        this.selectedTrade = null;
                    }
                }
            } else {
                this.selectedTrade = null;
            }
        });
    }
    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public getUrlModelStatus(currentUrl) {
        this.showModelBar =
            this.store.value.urlModelToggle[currentUrl] &&
            this.store.value.urlModelToggle[currentUrl] === true;
    }

    onRunModelConfig(showModelBar) {
        this.showModelOverlay = false;
        this.showModelBar = showModelBar;
        this.modellingConfig = this.store.value.modellingConfig;
        this.getMinAndMaxTail(this.modellingConfig);
    }

    onCreateNewModel() {
        this.getTradeSimulations(this.legalEntityName);
    }

    openModellingPanel() {
        this.showModelOverlay = !this.showModelOverlay;
        this.getTradeSimulations(this.legalEntityName);
    }

    onModellingTabClose() {
        if (this.showModelBar === true && this.showModelOverlay === false) {
            this.showModelBar = false;
            if (
                this.store.value.urlModelToggle !== undefined &&
                this.store.value.urlModelToggle !== null
            ) {
                this.store.value.urlModelToggle[this.router.url] =
                    this.showModelBar;
                this.store.set(
                    'urlModelToggle',
                    this.store.value.urlModelToggle
                );
            }
            this.reloadPageData();
        } else {
            this.showModelOverlay = false;
            this.showModelBar =
                this.store.value.urlModelToggle[this.router.url] === true
                    ? true
                    : false;
        }
    }

    onChangeButtonClick() {
        this.showModelBar = false;
        this.showModelOverlay = true;
    }

    onDownloadAllScenariosClick() {
        this.isLoading = true;
        console.log('download button clicked...');
        this.dataService.downloadAllScenariosFile().subscribe(
            (data) => {
                console.log('received download data. now saving file...');
                saveAs(data, 'MtM.zip');
                this.isLoading = false;
            },
            (error) => {
                this.isLoading = false;
                console.log('download failed. error: ' + error);
                this.errorPopup.show(
                    'Failed to download allScenarios. Please try again later.'
                );
            }
        );
    }

    onDownloadLogsClick() {
        this.isLoading = true;
        console.log('download button clicked for logs...');
        this.dataService.downloadLogsFile().subscribe(
            (data) => {
                console.log('received download data. now saving file...');
                saveAs(data, 'Logs.zip');
                this.isLoading = false;
            },
            (error) => {
                this.isLoading = false;
                console.log('download failed. error: ' + error);
                this.errorPopup.show(
                    'Failed to download logs. Please try again later.'
                );
            }
        );
    }

    public getMinAndMaxTail(modellingConfig: QueryConfig) {
        this.minTail = null;
        this.maxTail = null;
        if (modellingConfig.setForGroup) {
            this.minTail = _.min(
                _.pluck(modellingConfig.groupTailConfig, 'tail')
            );
            this.maxTail = _.max(
                _.pluck(modellingConfig.groupTailConfig, 'tail')
            );
        } else {
            for (const item of modellingConfig.entityTailConfigs) {
                let min = 0;
                let max = 0;
                min = _.min(_.pluck(item.yearRangeTails, 'tail'));
                max = _.max(_.pluck(item.yearRangeTails, 'tail'));
                if (!this.minTail || this.minTail > min) {
                    this.minTail = min;
                }
                if (!this.maxTail || this.maxTail < max) {
                    this.maxTail = max;
                }
            }
        }
    }

    public computeLegalEntityName(entityId: string): string {
        const found = _.find(
            this.user.legalEntities,
            (x: any) => x.entityId === entityId
        );
        if (found) {
            return found.dataName;
        }
        return null;
    }

    public getTitle(url: string) {
        if (url === '/overview') {
            this.title = 'Overview';
        } else if (url.indexOf('entitySummary') >= 0) {
            this.title = this.legalEntityName;
        } else if (url.indexOf('counterParty') >= 0) {
            this.title = this.legalEntityName + ' > ' + this.counterPartyName;
        }
    }

    public reloadPageData() {
        this.appEvents.triggerPageReload();
    }

    public getTradeSimulations(entity: string) {
        if (entity) {
            this.tradeDataLoading = true;
            this.dataService.getEntityTrades(entity).subscribe((x) => {
                this.tradeDataLoading = false;
                this.trades = x.entityTrades;
                this.trades.sort((a, b) =>
                    a.modelName.localeCompare(b.modelName)
                );
            });
        }
    }
}
